import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { PRICE_REFRESH_INTERVAL } from '../utils/constants';

/**
 * Custom hook to fetch ETH price with rate limiting and caching
 */
const useEthPrice = () => {
  const [ethPrice, setEthPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(true);
  const [error, setError] = useState(null);
  
  // Use refs to track last successful fetch
  const lastFetchTimeRef = useRef(0);
  const cachedPriceRef = useRef(null);
  
  useEffect(() => {
    let isMounted = true;
    let timeoutId = null;
    
    const fetchPrice = async () => {
      // Check if we need to fetch (rate limit)
      const now = Date.now();
      const timeSinceLastFetch = now - lastFetchTimeRef.current;
      
      // If it's been less than the refresh interval, use cached price
      if (cachedPriceRef.current && timeSinceLastFetch < PRICE_REFRESH_INTERVAL) {
        if (isMounted && !ethPrice) {
          console.log(`Using cached ETH price: ${cachedPriceRef.current}`);
          setEthPrice(cachedPriceRef.current);
          setIsLoadingPrice(false);
        }
        return;
      }
      
      try {
        setIsLoadingPrice(true);
        
        // Direct ETH price fetch with fallback options
        // Option 1: Try CoinGecko with JSONP (avoids CORS)
        const price = await fetchPriceWithFallbacks();
        
        if (isMounted) {
          setEthPrice(price);
          setError(null);
          
          // Update cache
          cachedPriceRef.current = price;
          lastFetchTimeRef.current = now;
          
          console.log(`Fetched ETH price: ${price}`);
        }
      } catch (err) {
        console.warn('Error fetching ETH price:', err.message);
        
        if (isMounted) {
          // Use cached price if available
          if (cachedPriceRef.current) {
            console.log(`Using cached ETH price after error: ${cachedPriceRef.current}`);
            setEthPrice(cachedPriceRef.current);
          } else {
            // Use a fallback price
            const fallbackPrice = 2750;
            console.log(`Using fallback ETH price: ${fallbackPrice}`);
            setEthPrice(fallbackPrice);
            cachedPriceRef.current = fallbackPrice;
          }
          
          setError('Could not update ETH price');
        }
      } finally {
        if (isMounted) {
          setIsLoadingPrice(false);
        }
      }
    };
    
    // Try multiple methods to get ETH price
    const fetchPriceWithFallbacks = async () => {
      // Method 1: Try direct API call (may fail due to CORS)
      try {
        const response = await axios.get('https://api.coingecko.com/api/v3/simple/price?ids=ethereum&vs_currencies=usd', {
          timeout: 3000
        });
        if (response.data && response.data.ethereum && response.data.ethereum.usd) {
          return response.data.ethereum.usd;
        }
      } catch (error) {
        console.log('Direct API call failed, trying alternative methods...');
      }
      
      // Method 2: Try Coinbase API (generally more reliable and CORS-friendly)
      try {
        const response = await axios.get('https://api.coinbase.com/v2/prices/ETH-USD/spot', {
          timeout: 3000
        });
        if (response.data && response.data.data && response.data.data.amount) {
          return parseFloat(response.data.data.amount);
        }
      } catch (error) {
        console.log('Coinbase API call failed...');
      }
      
      // Method 3: Try Binance API
      try {
        const response = await axios.get('https://api.binance.com/api/v3/ticker/price?symbol=ETHUSDT', {
          timeout: 3000
        });
        if (response.data && response.data.price) {
          return parseFloat(response.data.price);
        }
      } catch (error) {
        console.log('Binance API call failed...');
      }
      
      // If all methods fail, throw error to use fallback
      throw new Error('All ETH price fetch methods failed');
    };
    
    // Initial fetch
    fetchPrice();
    
    // Schedule next fetch
    const scheduleNextFetch = () => {
      timeoutId = setTimeout(() => {
        fetchPrice();
        if (isMounted) {
          scheduleNextFetch();
        }
      }, PRICE_REFRESH_INTERVAL);
    };
    
    scheduleNextFetch();
    
    // Cleanup
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [ethPrice]);
  
  return { ethPrice, isLoadingPrice, error };
};

export default useEthPrice;